import React, { useState } from 'react';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import { loadRatings } from '../actions';
import StarRating from './StarRating';
import BouncySpinner from 'scy/BouncySpinner';
import Pagination from './Pagination';
import { plainList } from 'scy/utils.mod.scss';
import { Body, H4 } from 'scy/text';
import { Avatar, Initial } from 'scy/UserAvatar';
import Checkmark from 'images/icons/controls/check.svg?react-svg';
import {
  accent,
  secondary,
  highlight,
  fontSizeXS,
  sansSerifRegular
} from 'scy/text/styles.mod.scss';
import { LightHR } from 'scy/HR';
import { textLeft, right } from 'scy/utils.mod.scss';
import { flex, centerItems } from 'scy/flex.mod.scss';
import { relative } from 'scy/position.mod.scss';
import margin from 'scy/margin.mod.scss';
import padding from 'scy/padding.mod.scss';
import styles from './ReviewsList.mod.scss';

const secondaryTextClass = `${sansSerifRegular} ${secondary} ${fontSizeXS}`;

export default function ReviewsList({ sku, reviews, pageCount }) {
  const dispatch = useDispatch();

  const [pageLoading, setPageLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  function loadPage(page) {
    setPageLoading(true);
    setCurrentPage(page);
    return dispatch(loadRatings({ sku_id: sku, page, isInit: false })).then(
      () => setPageLoading(false)
    );
  }

  return (
    <>
      {pageLoading ? (
        <div className={`${padding.top_lg} ${padding.bottom_lg} ${padding.md}`}>
          <BouncySpinner />
        </div>
      ) : (
        <ul className={plainList}>
          {reviews.map((review, idx) => (
            <ListItem key={idx} {...review} />
          ))}
        </ul>
      )}

      <Pagination
        pageCount={pageCount}
        currentPage={currentPage}
        loadPage={loadPage}
        disabled={pageLoading}
      />
    </>
  );
}

function ListItem({ review, title, rating, timeago, reviewer }) {
  return (
    <li className={textLeft}>
      <div className={`${right} ${secondaryTextClass}`}>{timeago}</div>
      <StarRating rating={rating} className={accent} />
      {title && (
        <H4 uppercase marginClass={classnames(margin.top_sm, margin.bottom_sm)}>
          {title}
        </H4>
      )}
      <Body marginClass={`${margin.top_sm} ${margin.bottom_sm}`}>{review}</Body>
      <ReviewerInfo {...reviewer} />
      <LightHR className={`${margin.top_md} ${margin.bottom_md}`} />
    </li>
  );
}

function ReviewerInfo(props) {
  const { first_name, last_name, verified_buyer, address } = props;

  return (
    <div className={`${flex} ${centerItems}`}>
      <ReviewerAvatar {...props} />
      <div>
        <Body marginClass={`${margin.top_0} ${margin.bottom_xxs}`}>
          {`${first_name}${last_name ? ` ${last_name}` : ''}`}
          {verified_buyer === 'yes' ? (
            <>
              {' '}
              - <span className={highlight}>Verified buyer</span>
            </>
          ) : null}
        </Body>
        <div className={secondaryTextClass}>{address}</div>
      </div>
    </div>
  );
}

function ReviewerAvatar({
  profile_picture,
  gravatar,
  first_name,
  last_name,
  verified_buyer
}) {
  if (!profile_picture && !gravatar && !first_name) {
    return null;
  }

  return (
    <div className={classnames(relative, margin.right_sm)}>
      {profile_picture ? (
        <Avatar src={profile_picture} className={styles.avatar} />
      ) : gravatar ? (
        <Avatar
          src={`https://www.gravatar.com/avatar/${gravatar}`}
          className={styles.avatar}
        />
      ) : (
        <Initial
          user={{ firstname: first_name, lastname: last_name }}
          includeLastInitial
          className={styles.avatar}
        />
      )}

      {verified_buyer === 'yes' ? (
        <div className={styles.checkmark}>
          <Checkmark width={10} height={8} />
        </div>
      ) : null}
    </div>
  );
}
